import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Clock, UtensilsCrossed, Plus, Trash2,
  MessageSquare, Send, Check, UserCog, Phone, User,
  FileDown, FileText, Printer, DollarSign, History, Share2, Copy, LinkIcon, AlertTriangle, Target,
  ClipboardCopy, Bookmark, CalendarPlus, QrCode, Download
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import ChatPanel from "../components/ChatPanel";
import { QRCodeSVG } from "qrcode.react";

import MealCard from "../components/event/MealCard";
import MealDialog from "../components/event/MealDialog";
import { exportEventCSV, exportEventPDF } from "../lib/eventExport";
import { ICON_MAP, DEFAULT_MEAL_TYPES } from "../lib/mealTypes";

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { authAxios, token, API, isAdmin, isSubAdmin, isTeamLead, isEventHost, canEdit, canDelete, canApprove } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [meals, setMeals] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [notes, setNotes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mealDialogOpen, setMealDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);
  const [mealForm, setMealForm] = useState({ meal_type: "", start_time: "", end_time: "", notes: "" });
  const [mealItems, setMealItems] = useState([]);
  const [addItemVendorId, setAddItemVendorId] = useState("none");
  const [noteForm, setNoteForm] = useState({ message: "", requested_changes: "" });
  const [shareToken, setShareToken] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [mealTypes, setMealTypes] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [editingMealId, setEditingMealId] = useState(null);

  const MEAL_TYPES = useMemo(() => {
    if (mealTypes.length > 0) {
      return mealTypes.map((mt) => ({
        value: mt.name,
        icon: ICON_MAP[mt.icon] || UtensilsCrossed,
        color: mt.color || "bg-muted text-muted-foreground",
        meal_type_id: mt.meal_type_id,
      }));
    }
    return DEFAULT_MEAL_TYPES.map((mt) => ({
      value: mt.value,
      icon: ICON_MAP[mt.icon] || UtensilsCrossed,
      color: mt.color,
    }));
  }, [mealTypes]);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const [eventRes, mealsRes, vendorsRes, notesRes, activityRes, mealTypesRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/meals`),
        api.get("/vendors"),
        api.get(`/events/${eventId}/notes`),
        api.get(`/events/${eventId}/activity`),
        api.get("/meal-types"),
      ]);
      setEvent(eventRes.data);
      setShareToken(eventRes.data.share_token || null);
      setMeals(mealsRes.data);
      setVendors(vendorsRes.data);
      setNotes(notesRes.data);
      setActivities(activityRes.data);
      setMealTypes(mealTypesRes.data);
      try {
        const mgrs = await api.get("/managers");
        setManagers(mgrs.data);
      } catch {}
    } catch (err) {
      toast.error("Failed to load event");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [eventId, authAxios]);

  // Get vendor's menu items for the add-item dropdown
  const addItemVendor = useMemo(() => {
    if (!addItemVendorId || addItemVendorId === "none") return null;
    return vendors.find((v) => v.vendor_id === addItemVendorId) || null;
  }, [addItemVendorId, vendors]);

  const addItemVendorMenu = useMemo(() => {
    return addItemVendor?.menu_items || [];
  }, [addItemVendor]);

  const addFoodItem = (vendorItem) => {
    const vendor = vendors.find((v) => v.vendor_id === addItemVendorId);
    setMealItems((prev) => {
      const existing = prev.find((i) => i.name === vendorItem.name && i.vendor_id === addItemVendorId);
      if (existing) {
        return prev.map((i) => i === existing ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        name: vendorItem.name,
        vendor_id: addItemVendorId,
        vendor_name: vendor?.name || "",
        price: Number(vendorItem.price) || 0,
        quantity: 1,
        category: vendorItem.category || "",
      }];
    });
  };

  const removeFoodItem = (index) => {
    setMealItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFoodItemQty = (index, delta) => {
    setMealItems((prev) => prev.map((item, i) => {
      if (i !== index) return item;
      const newQty = Math.max(1, item.quantity + delta);
      return { ...item, quantity: newQty };
    }));
  };

  const mealItemsTotal = useMemo(() => {
    return mealItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [mealItems]);

  const handleAddMeal = async (e) => {
    e.preventDefault();
    try {
      const api = authAxios();
      const payload = {
        day_number: selectedDay,
        meal_type: mealForm.meal_type,
        vendor_id: null,
        menu_items: mealItems.map((item) => ({
          name: item.name,
          vendor_id: item.vendor_id,
          vendor_name: item.vendor_name,
          price: item.price,
          quantity: item.quantity,
          category: item.category || "",
        })),
        notes: mealForm.notes,
        start_time: mealForm.start_time,
        end_time: mealForm.end_time,
      };
      if (editingMealId) {
        await api.put(`/events/${eventId}/meals/${editingMealId}`, payload);
        toast.success("Meal updated!");
      } else {
        await api.post(`/events/${eventId}/meals`, payload);
        toast.success("Meal added!");
      }
      setMealDialogOpen(false);
      resetMealForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save meal");
    }
  };

  const resetMealForm = () => {
    setMealForm({ meal_type: "", start_time: "", end_time: "", notes: "" });
    setMealItems([]);
    setAddItemVendorId("none");
    setEditingMealId(null);
  };

  const openEditMeal = (meal) => {
    setEditingMealId(meal.meal_id);
    setMealForm({
      meal_type: meal.meal_type || "",
      start_time: meal.start_time || "",
      end_time: meal.end_time || "",
      notes: meal.notes || "",
    });
    // Convert menu_items to mealItems format (handle both old string[] and new dict[] format)
    const items = (meal.menu_items || []).map((item) => {
      if (typeof item === "string") {
        return { name: item, vendor_id: meal.vendor_id || "", vendor_name: getVendorName(meal.vendor_id), price: getVendorItemPrice(meal.vendor_id, item) || 0, quantity: 1, category: "" };
      }
      return { name: item.name, vendor_id: item.vendor_id || "", vendor_name: item.vendor_name || "", price: Number(item.price) || 0, quantity: Number(item.quantity) || 1, category: item.category || "" };
    });
    setMealItems(items);
    setMealDialogOpen(true);
  };

  const handleDeleteMeal = async (mealId) => {
    if (!isAdmin) {
      toast.error("Only admins can delete meals");
      return;
    }
    try {
      const api = authAxios();
      await api.delete(`/events/${eventId}/meals/${mealId}`);
      toast.success("Meal removed");
      fetchData();
    } catch {
      toast.error("Failed to delete meal");
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      const api = authAxios();
      await api.post(`/events/${eventId}/notes`, noteForm);
      toast.success("Request sent successfully!");
      setNoteDialogOpen(false);
      setNoteForm({ message: "", requested_changes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
  };

  const handleAssignManager = async (managerId) => {
    try {
      const api = authAxios();
      await api.put(`/events/${eventId}/assign`, { managed_by: managerId });
      toast.success("Event manager assigned!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign manager");
    }
  };

  const getVendorName = (vendorId) => {
    const v = vendors.find((v) => v.vendor_id === vendorId);
    return v ? v.name : "";
  };

  const getVendorItemPrice = (vendorId, itemName) => {
    const v = vendors.find((v) => v.vendor_id === vendorId);
    if (!v || !v.menu_items) return null;
    const item = v.menu_items.find((i) => i.name === itemName);
    return item?.price || null;
  };

  // Calculate meal cost from items (handles both old and new format)
  const getMealCost = (meal) => {
    if (!meal.menu_items || meal.menu_items.length === 0) return 0;
    return meal.menu_items.reduce((sum, item) => {
      if (typeof item === "string") {
        const price = getVendorItemPrice(meal.vendor_id, item);
        return sum + (price ? Number(price) : 0);
      }
      return sum + (Number(item.price) || 0) * (Number(item.quantity) || 1);
    }, 0);
  };

  // ── Export Functions ──
  // ── Exports (implementations live in lib/eventExport.js) ──
  const exportCSV = () => exportEventCSV({ event, meals, getVendorName, getVendorItemPrice });
  const handleShare = async () => {
    setShareLoading(true);
    try {
      const api = authAxios();
      const res = await api.post(`/events/${eventId}/share`);
      const token = res.data.share_token;
      setShareToken(token);
      const shareUrl = `${window.location.origin}/shared/${token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard!");
    } catch {
      toast.error("Failed to generate share link");
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    try {
      const api = authAxios();
      await api.delete(`/events/${eventId}/share`);
      setShareToken(null);
      toast.success("Share link revoked");
    } catch {
      toast.error("Failed to revoke share link");
    }
  };

  const copyShareLink = async () => {
    if (!shareToken) return;
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied!");
  };

  const exportPDF = () =>
    exportEventPDF({ event, meals, costBreakdown, getVendorName, getVendorItemPrice, getMealCost });
  const handlePrint = () => {
    window.print();
  };

  const handleCloneEvent = async () => {
    try {
      const api = authAxios();
      const res = await api.post(`/events/${eventId}/clone`);
      toast.success("Event cloned!");
      navigate(`/events/${res.data.event_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clone event");
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const api = authAxios();
      await api.post("/templates", { name: templateName.trim(), event_id: eventId });
      toast.success("Saved as template!");
      setTemplateDialogOpen(false);
      setTemplateName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    }
  };

  const handleDownloadIcal = () => {
    const api = authAxios();
    const baseUrl = api.defaults.baseURL || "";
    const token = localStorage.getItem("auth_token");
    window.open(`${baseUrl}/events/${eventId}/ical?token=${token}`, "_blank");
    toast.success("Calendar file downloading...");
  };

  // ── Cost Calculation per Meal Category ──
  const costBreakdown = useMemo(() => {
    const breakdown = {};
    MEAL_TYPES.forEach(({ value }) => { breakdown[value] = 0; });
    let grandTotal = 0;
    meals.forEach((meal) => {
      const mealCost = getMealCost(meal);
      if (breakdown[meal.meal_type] !== undefined) {
        breakdown[meal.meal_type] += mealCost;
      }
      grandTotal += mealCost;
    });
    return { breakdown, grandTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meals, vendors]);

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-12 w-full">
        <div className="space-y-4">
          <div className="h-8 bg-muted rounded w-48 animate-pulse" />
          <div className="h-32 bg-muted rounded-xl animate-pulse" />
          <div className="h-64 bg-muted rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!event) return null;
  const days = event.days || [];

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="event-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/events")} data-testid="back-to-events-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground">{event.title}</h1>
            {/* Export & Print Buttons */}
            <div className="flex items-center gap-2 print:hidden flex-wrap" data-testid="export-buttons">
              {canEdit && (
                <Button variant="outline" size="sm" onClick={handleCloneEvent} data-testid="clone-event-btn">
                  <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" /> Clone
                </Button>
              )}
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => { setTemplateName(event.title); setTemplateDialogOpen(true); }} data-testid="save-template-btn">
                  <Bookmark className="h-3.5 w-3.5 mr-1.5" /> Template
                </Button>
              )}
              {canEdit && !shareToken && (
                <Button variant="outline" size="sm" onClick={handleShare} disabled={shareLoading} data-testid="share-event-btn">
                  <Share2 className="h-3.5 w-3.5 mr-1.5" /> {shareLoading ? "..." : "Share"}
                </Button>
              )}
              {canEdit && shareToken && (
                <>
                  <Button variant="outline" size="sm" onClick={copyShareLink} data-testid="copy-share-link-btn">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Link
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setQrDialogOpen(true)} data-testid="qr-code-btn">
                    <QrCode className="h-3.5 w-3.5 mr-1.5" /> QR
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={handleRevokeShare} data-testid="revoke-share-btn">
                    <LinkIcon className="h-3.5 w-3.5 mr-1.5" /> Revoke
                  </Button>
                </>
              )}
              <Button variant="default" size="sm" onClick={() => navigate(`/events/${eventId}/today`)} data-testid="today-view-btn">
                <Clock className="h-4 w-4 mr-1.5" />
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/events/${eventId}/attendees`)} data-testid="attendees-btn">
                <User className="h-4 w-4 mr-1.5" />
                Registrations
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/events/${eventId}/wrap-up`)} data-testid="wrapup-btn">
                <History className="h-4 w-4 mr-1.5" />
                Wrap-up
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadIcal} data-testid="ical-export-btn">
                <CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> iCal
              </Button>
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-pdf-btn">
                <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{event.start_date} to {event.end_date}</span>
            {event.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{event.location}</span>}
            <Badge variant="secondary">{days.length} day(s)</Badge>
            {event.status && (
              <Badge className={
                event.status === "approved" ? "bg-emerald-500/10 text-emerald-600" :
                event.status === "pending" ? "bg-amber-500/10 text-amber-600" :
                event.status === "complete" ? "bg-blue-500/10 text-blue-600" :
                "bg-red-500/10 text-red-600"
              }>{event.status.charAt(0).toUpperCase() + event.status.slice(1)}</Badge>
            )}
            {costBreakdown.grandTotal > 0 && (
              <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-700" data-testid="total-cost-badge">
                <DollarSign className="h-3 w-3 mr-0.5" /> Total: ${costBreakdown.grandTotal.toFixed(2)}
              </Badge>
            )}
          </div>
          {event.description && <p className="text-muted-foreground mt-3 text-base max-w-2xl">{event.description}</p>}
          <div className="flex flex-wrap gap-4 mt-3">
            {event.contact_name && (
              <div className="flex items-center gap-2 text-sm bg-muted/50 px-3 py-1.5 rounded-lg" data-testid="event-contact-info">
                <User className="h-4 w-4 text-blue-500" />
                <span className="text-foreground font-medium">{event.contact_name}</span>
                {event.contact_phone && <span className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" /> {event.contact_phone}</span>}
              </div>
            )}
            {event.managed_by_name && (
              <div className="flex items-center gap-2 text-sm bg-primary/5 px-3 py-1.5 rounded-lg" data-testid="event-managed-by-info">
                <UserCog className="h-4 w-4 text-primary" />
                <span className="text-foreground font-medium">Managed by: {event.managed_by_name}</span>
              </div>
            )}
          </div>
          {canApprove && (
            <div className="mt-3 flex items-center gap-2" data-testid="assign-manager-section">
              <UserCog className="h-4 w-4 text-muted-foreground" />
              <Select value={event.managed_by || "unassigned"} onValueChange={(v) => handleAssignManager(v === "unassigned" ? "" : v)}>
                <SelectTrigger className="w-56 h-9" data-testid="assign-manager-select"><SelectValue placeholder="Assign Event Manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {managers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role === "admin" ? "Admin" : m.role === "sub_admin" ? "Sub Admin" : "Team Lead"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {isEventHost && (
          <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
            <Button variant="outline" className="shrink-0 print:hidden" onClick={() => setNoteDialogOpen(true)} data-testid="send-note-btn">
              <MessageSquare className="h-4 w-4 mr-2" /> Request Change
            </Button>
            <DialogContent data-testid="note-dialog">
              <DialogHeader><DialogTitle className="font-heading">Request Menu Change</DialogTitle></DialogHeader>
              <form onSubmit={handleAddNote} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea placeholder="Describe what you'd like to change..." value={noteForm.message} onChange={(e) => setNoteForm({ ...noteForm, message: e.target.value })} required rows={3} data-testid="note-message-input" />
                </div>
                <div className="space-y-2">
                  <Label>Requested Changes (optional)</Label>
                  <Textarea placeholder="Specific menu items, vendors, dietary needs..." value={noteForm.requested_changes} onChange={(e) => setNoteForm({ ...noteForm, requested_changes: e.target.value })} rows={2} data-testid="note-changes-input" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-note-btn"><Send className="h-4 w-4 mr-2" /> Send Request</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Day Tabs with Meal Grid */}
      <Tabs defaultValue="1" className="w-full" data-testid="event-days-tabs">
        <div className="flex items-center justify-between mb-4">
          <TabsList data-testid="day-tabs-list">
            {days.map((d) => (
              <TabsTrigger key={d.day_number} value={String(d.day_number)} data-testid={`day-tab-${d.day_number}`}>
                Day {d.day_number}
                <span className="hidden sm:inline ml-1 text-xs text-muted-foreground">({d.day_name?.slice(0, 3)})</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {canEdit && (
            <MealDialog
              open={mealDialogOpen}
              onOpenChange={(o) => { setMealDialogOpen(o); if (!o) resetMealForm(); }}
              onSubmit={handleAddMeal}
              editingMealId={editingMealId}
              days={days}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              mealForm={mealForm}
              setMealForm={setMealForm}
              MEAL_TYPES={MEAL_TYPES}
              vendors={vendors}
              addItemVendorId={addItemVendorId}
              setAddItemVendorId={setAddItemVendorId}
              addItemVendor={addItemVendor}
              addItemVendorMenu={addItemVendorMenu}
              addFoodItem={addFoodItem}
              removeFoodItem={removeFoodItem}
              updateFoodItemQty={updateFoodItemQty}
              mealItems={mealItems}
              mealItemsTotal={mealItemsTotal}
            />
          )}
        </div>

        {days.map((day) => {
          const dayMeals = meals.filter((m) => m.day_number === day.day_number);
          return (
            <TabsContent key={day.day_number} value={String(day.day_number)} data-testid={`day-content-${day.day_number}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {MEAL_TYPES.map(({ value, icon: Icon, color }) => {
                  const meal = dayMeals.find((m) => m.meal_type === value);
                  const mealCost = meal ? getMealCost(meal) : 0;
                  return (
                    <MealCard
                      key={value}
                      mealType={value}
                      Icon={Icon}
                      color={color}
                      meal={meal}
                      mealCost={mealCost}
                      dayNumber={day.day_number}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      getVendorName={getVendorName}
                      getVendorItemPrice={getVendorItemPrice}
                      onEdit={(m) => { setSelectedDay(day.day_number); openEditMeal(m); }}
                      onDelete={handleDeleteMeal}
                      onAdd={(type) => {
                        setSelectedDay(day.day_number);
                        resetMealForm();
                        setMealForm((f) => ({ ...f, meal_type: type }));
                        setMealDialogOpen(true);
                      }}
                    />
                  );
                })}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Cost Breakdown */}
      {(costBreakdown.grandTotal > 0 || event.budget_limit) && (
        <Card className="border-border/50 shadow-sm" data-testid="cost-breakdown-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              Cost Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Budget Progress Bar */}
            {event.budget_limit && (
              <div className="mb-5 p-4 rounded-lg border border-border/50 bg-muted/20" data-testid="budget-tracker">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">Budget</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    ${costBreakdown.grandTotal.toFixed(2)} / ${event.budget_limit.toFixed(2)}
                  </span>
                </div>
                {(() => {
                  const pct = Math.min((costBreakdown.grandTotal / event.budget_limit) * 100, 100);
                  const overBudget = costBreakdown.grandTotal > event.budget_limit;
                  const nearBudget = pct >= 80 && !overBudget;
                  const barColor = overBudget ? "bg-red-500" : nearBudget ? "bg-amber-500" : "bg-emerald-500";
                  return (
                    <>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-muted-foreground">{pct.toFixed(0)}% used</span>
                        {overBudget && (
                          <span className="text-xs font-medium text-red-600 flex items-center gap-1" data-testid="over-budget-alert">
                            <AlertTriangle className="h-3 w-3" /> Over budget by ${(costBreakdown.grandTotal - event.budget_limit).toFixed(2)}
                          </span>
                        )}
                        {nearBudget && (
                          <span className="text-xs font-medium text-amber-600 flex items-center gap-1" data-testid="near-budget-alert">
                            <AlertTriangle className="h-3 w-3" /> Nearing budget limit
                          </span>
                        )}
                        {!overBudget && !nearBudget && (
                          <span className="text-xs text-emerald-600">${(event.budget_limit - costBreakdown.grandTotal).toFixed(2)} remaining</span>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {costBreakdown.grandTotal > 0 && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {MEAL_TYPES.map(({ value, icon: Icon, color }) => {
                    const cost = costBreakdown.breakdown[value] || 0;
                    return (
                      <div
                        key={value}
                        className={`p-3 rounded-lg border border-border/50 ${cost > 0 ? "bg-card" : "bg-muted/30 opacity-60"}`}
                        data-testid={`cost-${value.replace(/\s/g, "-").toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-1.5 rounded-md ${color}`}><Icon className="h-3.5 w-3.5" /></div>
                          <span className="text-xs font-medium text-muted-foreground">{value}</span>
                        </div>
                        <p className={`text-lg font-bold ${cost > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                          ${cost.toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between" data-testid="cost-grand-total">
                  <span className="text-sm font-medium text-muted-foreground">Total Event Cost</span>
                  <span className="text-2xl font-bold text-emerald-600 flex items-center">
                    <DollarSign className="h-5 w-5" />{costBreakdown.grandTotal.toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notes Section */}
      {notes.length > 0 && (
        <Card className="border-border/50 shadow-sm" data-testid="event-notes-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" /> Change Requests ({notes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {notes.map((note) => (
              <div key={note.note_id} className={`p-3 rounded-lg border text-sm ${note.status === "resolved" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`} data-testid={`note-item-${note.note_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground">{note.creator_name}</span>
                      <Badge variant="secondary" className="text-[10px]">{note.creator_role}</Badge>
                      <Badge className={note.status === "resolved" ? "bg-emerald-500/10 text-emerald-600 text-[10px]" : "bg-amber-500/10 text-amber-600 text-[10px]"}>{note.status}</Badge>
                    </div>
                    <p className="text-foreground">{note.message}</p>
                    {note.requested_changes && <p className="text-muted-foreground mt-1 text-xs italic">Changes: {note.requested_changes}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(note.created_at).toLocaleString()}</p>
                  </div>
                  {(isAdmin || isSubAdmin || isTeamLead) && note.status === "pending" && (
                    <Button size="sm" variant="outline" className="shrink-0 print:hidden" onClick={async () => {
                      try { const api = authAxios(); await api.put(`/notes/${note.note_id}/resolve`); toast.success("Note resolved"); fetchData(); } catch { toast.error("Failed to resolve"); }
                    }} data-testid={`resolve-note-inline-${note.note_id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Resolve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Activity Timeline */}
      {activities.length > 0 && (
        <Card className="border-border/50 shadow-sm" data-testid="event-activity-timeline">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <History className="h-5 w-5 text-primary" /> Activity Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative pl-6">
              <div className="absolute left-2.5 top-1 bottom-1 w-px bg-border" />
              {activities.map((act, idx) => {
                const actionConfig = {
                  created: { color: "bg-emerald-500", label: "Created" },
                  updated: { color: "bg-blue-500", label: "Updated" },
                  approved: { color: "bg-emerald-500", label: "Approved" },
                  rejected: { color: "bg-red-500", label: "Rejected" },
                  deleted: { color: "bg-red-500", label: "Deleted" },
                  status_approved: { color: "bg-emerald-500", label: "Approved" },
                  status_rejected: { color: "bg-red-500", label: "Rejected" },
                  status_complete: { color: "bg-blue-500", label: "Completed" },
                  status_pending: { color: "bg-amber-500", label: "Set to Pending" },
                  meal_added: { color: "bg-green-500", label: "Meal Added" },
                  meal_updated: { color: "bg-blue-500", label: "Meal Updated" },
                  meal_deleted: { color: "bg-red-500", label: "Meal Removed" },
                  note_added: { color: "bg-amber-500", label: "Note Submitted" },
                  note_resolved: { color: "bg-emerald-500", label: "Note Resolved" },
                  shared: { color: "bg-indigo-500", label: "Shared" },
                  share_revoked: { color: "bg-slate-500", label: "Share Revoked" },
                };
                const config = actionConfig[act.action] || { color: "bg-muted-foreground", label: act.action };
                return (
                  <div key={act.activity_id || idx} className="relative pb-5 last:pb-0" data-testid={`activity-item-${act.activity_id}`}>
                    <div className={`absolute -left-3.5 top-1 h-3 w-3 rounded-full ring-2 ring-background ${config.color}`} />
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <span className="text-sm font-medium text-foreground">{config.label}</span>
                      <span className="text-xs text-muted-foreground">by {act.user_name} ({act.user_role})</span>
                      <span className="text-xs text-muted-foreground/70">{new Date(act.created_at).toLocaleString()}</span>
                    </div>
                    {act.details && (
                      <p className="text-xs text-muted-foreground mt-0.5">{act.details}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Panel */}
      <div className="print:hidden">
        <ChatPanel eventId={eventId} />
      </div>

      {/* Save as Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Save as Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Standard Community Dinner" data-testid="template-name-input" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate} disabled={!templateName.trim()} data-testid="confirm-save-template-btn">
                <Bookmark className="h-4 w-4 mr-1" /> Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-xs" data-testid="qr-code-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">Share QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {shareToken && (
              <QRCodeSVG
                value={`${window.location.origin}/shared/${shareToken}`}
                size={200}
                level="H"
                data-testid="qr-code-svg"
              />
            )}
            <p className="text-xs text-muted-foreground text-center">Scan to view the event schedule</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
